// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { convertToLocaleId, handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import {
    ObjectTypeV1 as ObjectType,
    AlertOutput,
    AlertStatusStage,
    CustomStatusMapType,
    PossibleNamedQuery,
    PermissionsEnum,
} from 'thehive-sdk';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import { Badge, Space } from 'antd';
import RouteBuilder from '@Services/RouteBuilder';
import { Link, useHistory } from 'react-router-dom';
import { computeActionList } from '@Organisms/Alert/actionList';
import ActionList from '@Molecules/ActionList/ActionList';
import FormatMultipleDate from '@Molecules/Format/FormatMultipleDate/FormatMultipleDate';
import { FormatDate } from '@Molecules/Format';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import QuickFilterList from '@Molecules/QuickFilterList/QuickFilterList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import AlertStats from '@Organisms/Alert/AlertStats/AlertStats';
import { AlertListTitle } from '@Organisms/Alert/AlertListTitle/AlertListTitle';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import THTag from '@Atoms/THTag/THTag';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import LinkButton from '@Atoms/Buttons/LinkButton/LinkButton';
import { computeEventList } from '@Organisms/Alert/eventList';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { HTMLColors } from '@Constants/colors';
import { QuickFiltersAvailable } from '@Hooks/useQuickFilters';
import IconCount from '@Atoms/IconCount/IconCount';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { ICONS } from '@Constants/iconsApp';
import { AlertDrawer } from '@Organisms/Alert/AlertDrawer/AlertDrawer';
import { useDrawer } from '@Hooks/useDrawer';
import { MultipleDisplayEntityInfo } from '@Molecules/MultipleDisplayEntityInfo/MultipleDisplayEntityInfo';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ScopeContext } from '@Context/ScopeProvider';
import { useQuery } from 'react-query';
import ListProvider from '@Context/ListProvider';
import { RouteType } from '@Types';
import Shortify from '@Atoms/Shortify/Shortify';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useCurrentUser } from '@Queries/userQueries';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';
import usePageTitle from '@Hooks/usePageTitle';

import './AlertList.scss';

const AlertsList = (): ReactElement => {
    const i = useLocalMessage();

    const { visible, setVisible, data, setData } = useDrawer<AlertOutput>({});

    const history = useHistory();
    const { data: describe } = useListAllFields();
    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_ALERT_UPDATE,
        PermissionsEnum.MANAGE_ALERT_RESTART,
    ]);

    const [alertStatus, setAlertStatus] = React.useState<CustomStatusMapType>({});

    const config = useConfigList<AlertOutput>({
        selector: {
            head: [{ name: 'listAlert' }],
            extraData: ['importDate', 'caseNumber', 'status', 'procedureCount'],
            name: 'get-all-alerts',
            sort: ['-_createdAt'],
        },
        controller: client.AlertController,
        storageId: StorageFilterEnum.ALERT,
        objectType: ObjectType.ALERT,
    });

    const {
        customStatus: {
            alert: { list: customStatusList },
        },
    } = useContext(ScopeContext);

    const {
        storagePageManager,
        storageViewManager,
        listRefetch,
        dataSource = [],
        columnsRenderer,
        status = 'loading',
        countRefetch,
        quickFilterListRenderer,
        rowSelectionCustomRenderer,
        stateSelectedKeys: { selectedKeys },
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        table,
        filterManager,
        paginationManager,
        quickFilters,
        queryParams,
    } = config;

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const { setDefaultDisplay } = storageViewManager || {};

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.AlertController,
                history,
                locale: i,
                onSuccess: (count, message) => {
                    count && countRefetch();
                    message && openMessage('success', message);
                    listRefetch?.();
                },
                onError: handleNotificationError,
            }),
        [countRefetch, history, i, listRefetch],
    );

    const {
        page: storagePage,
        setDisplay,
        setStatsIsOpen,
        setFilterIsOpen,
        setAutoRefresh,
        setOnLoadView,
    } = storagePageManager || { page: null };

    const [showMeta, DisplayMeta, loadVisibleStateMeta] = useDisplayParams({
        type: { label: i('alert.table.columns.type'), visible: storagePage?.display?.type ?? true },
        source: { label: i('alert.table.columns.source'), visible: storagePage?.display?.source ?? true },
        reference: { label: i('common.reference'), visible: storagePage?.display?.reference ?? true },
    });

    const [showDetails, DisplayDetails, loadVisibleStateDetails] = useDisplayParams({
        observables: { label: i('common.observables'), visible: storagePage?.display?.observables ?? true },
        ttp: { label: i('common.ttp'), visible: storagePage?.display?.ttp ?? true },
    });

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        occurred: { label: i('common.occurred-date'), visible: storagePage?.display?.occurred ?? true },
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });

    const [showTitle, DisplayTitle, loadVisibleStateTitle] = useDisplayParams({
        tags: { label: i('common.tags'), visible: storagePage?.display?.tags ?? true },
        customFields: { label: i('common.custom-fields'), visible: storagePage?.display?.customFields ?? true },
    });

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localStatsIsOpen, setLocalStatsIsOpen] = useState(storagePage?.statsIsOpen || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);
    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
        setLocalStatsIsOpen(viewContent?.statsIsOpen || false);

        loadVisibleStateMeta({
            type: viewContent?.display?.type ?? true,
            source: viewContent?.display?.source ?? true,
            reference: viewContent?.display?.reference ?? true,
        });

        loadVisibleStateDetails({
            observables: viewContent?.display?.observables ?? true,
            ttp: viewContent?.display?.ttp ?? true,
        });

        loadVisibleStateDates({
            occurred: viewContent?.display?.occurred ?? true,
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });

        loadVisibleStateTitle({
            tags: viewContent?.display?.tags ?? true,
            customFields: viewContent?.display?.customFields ?? true,
        });
    });

    const configQuickFilterList = useMemo(
        () => [
            {
                title: i('filter.quick.alert.new'),
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'stage', operator: 'any', values: [AlertStatusStage.NEW] });
                },
            },
            {
                title: i('filter.quick.alert.in-progress'),
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'stage', operator: 'any', values: [AlertStatusStage.IN_PROGRESS] });
                },
            },
            {
                title: i('filter.quick.alert.imported'),
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'stage', operator: 'any', values: [AlertStatusStage.IMPORTED] });
                },
            },
            {
                title: i('filter.quick.alert.closed'),
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'stage', operator: 'any', values: [AlertStatusStage.CLOSED] });
                },
            },
            {
                title: i('filter.quick.alert.my-alerts'),
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'assignee', operator: 'any', values: [currentUser?.login] });
                },
            },
            {
                title: i('filter.quick.alert.unassigned'),
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'assignee', operator: 'is empty' });
                },
            },
        ],
        [i, currentUser],
    );

    const actionListFunc = useMemo(
        () => computeActionList(i, history, alertStatus, eventList, hasPermissions, currentUser),
        [alertStatus, eventList, history, currentUser, i, hasPermissions],
    );

    const actionList = actionListFunc(
        dataSource.filter((data: { _id: string }) => selectedKeys.includes(data['_id'])),
        (count) => {
            listRefetch?.();
            table?.setSelectedKeys?.([]);
            if (count) {
                countRefetch?.();
            }
        },
    );

    const quickFilterList = quickFilterListRenderer(configQuickFilterList);

    const columns = columnsRenderer([
        {
            title: [{ label: i('alert.table.columns.status'), sortField: 'status' }],
            className: 'alert-list__column-status',
            render: ({ extraData, _createdAt }, { custom: filterCustom }): ReactElement => (
                <div
                    className="alert-list__status"
                    style={{ [`--color-status` as string]: extraData?.status?.colour || HTMLColors.DARK_GRAY }}
                >
                    <ColorLabel
                        label={i({
                            id: `alert.data.status.${convertToLocaleId(extraData?.status?.value || '')}`,
                            defaultMessage: extraData?.status?.value,
                        })}
                        className="status-tag"
                        color={extraData?.status?.colour || HTMLColors.DARK_GRAY}
                        onClick={(): void | null =>
                            extraData?.status
                                ? filterCustom({
                                      field: 'status',
                                      values: [extraData?.status.value],
                                      operator: 'any',
                                  })
                                : null
                        }
                    />
                    {extraData?.importDate && (
                        <THTooltip title={i('alert.table.columns.date.imported')} placement="bottom">
                            <div className="alert-list__column-date__from">
                                <ICONS.ClockCircleOutlined />
                                <FormatDate date={_createdAt} endDate={extraData?.importDate} onlyFrom withoutSuffix />
                            </div>
                        </THTooltip>
                    )}
                </div>
            ),
        },
        {
            title: [
                { label: i('alert.table.columns.severity'), sortField: 'severity' },
                { label: i('alert.table.columns.title'), sortField: 'title' },
            ],
            className: 'alert-list__column-title column_preview',
            optionalRender: DisplayTitle,
            render: (
                data,
                { tags: filterTags, customField: filterCustomField, severity: filterSeverity },
            ): ReactElement => (
                <AlertListTitle
                    data={data}
                    onTagsClick={(tagValue, event): void => {
                        const isAltPressed = event.altKey;
                        filterTags([tagValue], isAltPressed);
                    }}
                    customFieldsIsOpen={showTitle?.customFields}
                    tagsIsOpen={showTitle?.tags}
                    onCustomFieldsClick={(customField): void => filterCustomField(customField)}
                    onPreviewClick={() => {
                        setData(data);
                        setVisible(true);
                    }}
                    onSeverityClick={filterSeverity}
                />
            ),
        },
        {
            title: [{ label: i('alert.table.columns.case') }],
            className: 'alert-list__column-case',
            render: ({ caseId, extraData }): ReactElement => (
                <THTag
                    value={extraData?.caseNumber ? `# ${extraData?.caseNumber}` : '-'}
                    onClick={
                        caseId ? (): void => history.push(RouteBuilder.compute('cases.details', { caseId })) : undefined
                    }
                />
            ),
        },
        {
            title: [
                { label: i('alert.table.columns.type'), sortField: 'type', hide: !showMeta.type },
                { label: i('alert.table.columns.source'), sortField: 'source', hide: !showMeta.source },
                { label: i('common.reference'), sortField: 'sourceRef', hide: !showMeta.reference },
            ],
            optionalRender: DisplayMeta,
            className: 'alert-list__column-meta',
            render: ({ type, source, sourceRef, externalLink }, { custom }): ReactElement => (
                <Space direction="vertical">
                    {showMeta.type && (
                        <LinkButton onClick={(): void => custom({ field: 'type', operator: 'any', values: [type] })}>
                            {type}
                        </LinkButton>
                    )}

                    {showMeta.source && (
                        <LinkButton
                            onClick={(): void => custom({ field: 'source', operator: 'any', values: [source] })}
                        >
                            {source}
                        </LinkButton>
                    )}

                    {showMeta.reference && (
                        <Space>
                            <Shortify line={sourceRef} maxSize={20} />{' '}
                            {externalLink && (
                                <ExternalLink url={externalLink}>
                                    <ICONS.ExternalLinkIcon />
                                </ExternalLink>
                            )}
                        </Space>
                    )}
                </Space>
            ),
        },
        {
            title: i('general.details'),
            className: 'alert-list__column-details',
            optionalRender: DisplayDetails,
            render: ({ observableCount, extraData, _id: alertId }): ReactElement => (
                <MultipleDisplayEntityInfo
                    className="alert-list__details"
                    data={[
                        {
                            label: i('common.observables'),
                            count: observableCount,
                            hide: !showDetails.observables,
                            link: RouteBuilder.compute('alerts.observables', { alertId }),
                        },
                        {
                            label: i('common.ttp'),
                            count: extraData?.procedureCount,
                            hide: !showDetails.ttp,
                            link: RouteBuilder.compute('alerts.procedures', { alertId }),
                        },
                    ]}
                />
            ),
        },
        {
            title: [{ label: i('case.list.table.columns.assignee'), sortField: 'assignee' }],
            className: 'column-assigneeProps',
            render: ({ assignee }, { assignee: filterAssignee }): ReactElement => (
                <AssigneeInfo assignee={assignee} avatarOnly onClick={() => filterAssignee(assignee)} />
            ),
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'O.', sortField: 'date', hide: !showDates.occurred },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            optionalRender: DisplayDates,
            className: 'alert-list__column-date',
            render: ({ date = 0, _createdAt = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'O.',
                            value: date,
                            onClick: (): void => filterDate(date, 'date'),
                            hide: !showDates.occurred,
                        },
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates.createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates.updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'alert-list__column-actions',
            render: (alert): ReactElement | null =>
                hasPermissions ? (
                    <ActionsList
                        actionList={actionListFunc([alert], (count) => {
                            listRefetch();
                            if (count) {
                                countRefetch();
                            }
                        })}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
    });

    const queryNewAlertParams = {
        selector: {
            head: [{ name: 'listAlert' as PossibleNamedQuery }],
            name: 'new-alerts',
        },
        filters: [
            {
                field: 'stage',
                operator: 'eq',
                type: 'string',
                values: 'New',
                describeAttribute: describe?.alert.attributes.find(({ name }) => name === 'stage') as any,
            },
        ],
    };

    const query = useQuery('new-alerts', () => client.AlertController.count(queryNewAlertParams), {
        refetchInterval: false,
        refetchOnWindowFocus: false,
    });

    const { data: count = 0, refetch } = query;

    useEffect(() => {
        setDisplay?.('occurred', showDates.occurred);
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        setDisplay?.('tags', showTitle.tags);
        setDisplay?.('customFields', showTitle.customFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTitle]);

    useEffect(() => {
        setDisplay?.('type', showMeta.type);
        setDisplay?.('source', showMeta.source);
        setDisplay?.('reference', showMeta.reference);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMeta]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            tags: showTitle.tags,
            customFields: showTitle.customFields,
            occurred: showDates.occurred,
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
            type: showMeta.type,
            source: showMeta.source,
            reference: showMeta.reference,
        });
    }, [storagePage, showTitle, showDates, showMeta, setDefaultDisplay]);

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    useEffect(() => {
        setStatsIsOpen?.(localStatsIsOpen);
    }, [localStatsIsOpen]); // eslint-disable-line

    useEffect(() => {
        if (customStatusList.length) {
            const alertStatusMap = customStatusList.reduce<CustomStatusMapType>((acc, curr) => {
                if (curr.stage === AlertStatusStage.IMPORTED) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setAlertStatus(alertStatusMap);
        }
    }, [customStatusList]);

    usePageTitle(i('page-title.alerts.list', { count }));

    useEffect(() => {
        refetch();
    }, [refetch]);

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                        <QuickFilterList quickFilterList={quickFilterList} />

                        <ExportButton
                            onClick={() => {
                                setExportDrawerVisible(true);
                            }}
                            objectType="Alert"
                            selectedItems={dataSource.reduce((acc: string[], data) => {
                                if (selectedKeys.includes(data['_id'])) {
                                    acc.push(data['_id']);
                                }
                                return acc;
                            }, [])}
                            queryParams={queryParams}
                            visible={exportDrawerVisible}
                            afterVisible={setExportDrawerVisible}
                            listNumber={table.count}
                        />

                        <SelectedInfo count={selectedKeys?.length} label={i('common.selected')} />
                        <ActionList actions={actionList} scope="list" hidden={!selectedKeys?.length} />
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        {!storagePage?.autoRefresh && (
                            <Badge dot={needRefresh}>
                                <THTooltip
                                    title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                    placement="bottom"
                                >
                                    <THButton
                                        size="small"
                                        type={needRefresh ? 'primary' : 'default'}
                                        shape="circle"
                                        onClick={() => {
                                            listRefetch();
                                            countRefetch();
                                            setNeedRefresh(false);
                                        }}
                                    >
                                        <ICONS.SyncOutlined />
                                    </THButton>
                                </THTooltip>
                            </Badge>
                        )}
                        <ManageSwitch
                            label={i('common.auto-refresh')}
                            value={localAutoRefresh}
                            onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                            icon={<ICONS.SyncOutlined />}
                        />

                        <ManageSwitch
                            label={i('common.stats')}
                            value={localStatsIsOpen}
                            onChange={(stats): void => setLocalStatsIsOpen(stats)}
                            icon={<ICONS.PieChartOutlined />}
                        />
                        <ManageSwitch
                            label={i('common.filters')}
                            value={localFilterIsOpen}
                            onChange={(editFilters): void => setLocalFilters(editFilters)}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {localStatsIsOpen && <AlertStats quickFilters={quickFilters} />}
                        {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.ALERT]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>
                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    rowSelection={hasPermissions ? rowSelection : undefined}
                    emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('alert.none')} />}
                />
                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {data && visible && (
                <AlertDrawer
                    data={data}
                    visible={visible}
                    afterVisible={setVisible}
                    eventList={eventList}
                    hasPermissions={hasPermissions}
                    actionList={actionListFunc(
                        [data],
                        (count) => {
                            if (count) {
                                countRefetch();
                            }
                            listRefetch();
                        },
                        'list',
                    )}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('alerts.default');

const MenuLink = (): ReactElement => {
    const local = useLocalMessage();
    return <Link to={path}>{local('mainLayout.userMenu.alerts')}</Link>;
};

const MenuIcon = (): ReactElement => {
    const { data: describeData } = useListAllFields();

    const queryParams = {
        selector: {
            head: [{ name: 'listAlert' as PossibleNamedQuery }],
            name: 'new-alerts',
        },
        filters: [
            {
                field: 'stage',
                operator: 'eq',
                type: 'string',
                values: 'New',
                describeAttribute: describeData?.alert.attributes.find(({ name }) => name === 'stage') as any,
            },
        ],
    };

    const query = useQuery('new-alerts', () => client.AlertController.count(queryParams), {
        refetchInterval: false,
        refetchOnWindowFocus: false,
    });

    const { data: count = 0, refetch } = query;

    useStreamObserver({ objectType: STREAM_EVENTS.ALERT }, (data): void => {
        const needUpdate = data.some(({ base }) => {
            const { action, details } = base;
            return (
                ['create', 'delete'].includes(action) ||
                (action === 'update' && Object.keys(details).includes('status'))
            );
        });

        if (needUpdate) {
            refetch();
        }
    });

    useEffect(() => {
        refetch();
    }, [refetch]);

    return (
        <IconCount count={count}>
            <ICONS.AlertIcon />
        </IconCount>
    );
};

export const Route: RouteType = {
    path,
    menu: {
        key: 'alerts',
        icon: <MenuIcon />,
        render: <MenuLink />,
        selectTab: path,
    },
    breadcrumb: { label: 'breadcrumb.global.alerts' },
    content: <AlertsList />,
    Context: ListProvider,
    space: ['user'],
    exact: true,
};
